import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { ROLES } from "../../../constants/roles";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import MasterPageBreadcrumb from "../../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../../layout/master/masterRouteMeta";
import MasterSpinner from "../../../components/Master/MasterSpinner";
import MasterModal from "../../../components/Master/MasterModal";
import MasterConfirmationModal from "../../../components/Master/MasterConfirmationModal";
import AuthField from "../../../components/Auth/AuthField";
import "./plans-page.css";

const emptyForm = {
	name: "",
	priceMonthly: "",
	priceQuarterly: "",
	priceSemiannual: "",
	priceAnnual: "",
	monthlyTokens: "",
	active: true,
};

const PLAN_COLORS = ["#6366f1", "#8b5cf6", "#06b6d4", "#f59e0b"];

const PERIOD_ROWS = [
	{ key: "priceQuarterly", labelKey: "master.plans.colPriceQuarterly" },
	{ key: "priceSemiannual", labelKey: "master.plans.colPriceSemiannual" },
	{ key: "priceAnnual", labelKey: "master.plans.colPriceAnnual" },
];

function IconPlus() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconTrash() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
		</svg>
	);
}

function formatPrice(value) {
	if (value === null || value === undefined || value === "") return "—";
	const n = Number(value);
	if (Number.isNaN(n)) return "—";
	return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatTokens(value) {
	return `${Number(value || 0).toLocaleString("pt-BR")} ${i18n.t("master.plans.tokensShort")}`;
}

function discountVsMonthly(periodValue, monthlyValue) {
	const period = Number(periodValue);
	const monthly = Number(monthlyValue);
	if (!monthly || Number.isNaN(monthly) || monthly <= 0) return null;
	if (Number.isNaN(period) || period <= 0) return null;
	const pct = Math.round((1 - period / monthly) * 100);
	if (pct <= 0) return null;
	return pct;
}

function toFormPrice(value) {
	if (value === null || value === undefined) return "";
	return String(value);
}

function toPayloadPrice(value) {
	if (value === "" || value === null || value === undefined) return 0;
	return Number(value);
}

function planColor(index) {
	return PLAN_COLORS[index % PLAN_COLORS.length];
}

function PlanCard({ plan, color, deleting, onEdit, onDelete }) {
	const [hovered, setHovered] = useState(false);

	return (
		<article
			className="mp-card"
			style={{
				borderColor: hovered ? `${color}55` : undefined,
				boxShadow: hovered ? `0 8px 32px ${color}22` : undefined,
			}}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<div
				className="mp-card__accent"
				style={{ background: `linear-gradient(90deg, ${color}, ${color}55)` }}
			/>

			<div className="mp-card__header">
				<div className="min-w-0">
					<div className="mp-card__name-row">
						<h3 className="mp-card__name">{plan.name}</h3>
					</div>
					<div className="mp-card__meta">
						<span>#{plan.id}</span>
						<span className="mp-card__meta-sep">·</span>
						<span>{formatTokens(plan.monthlyTokens)}</span>
					</div>
				</div>
				<span
					className={`mp-card__badge ${
						plan.active ? "mp-card__badge--active" : "mp-card__badge--inactive"
					}`}
				>
					{plan.active
						? i18n.t("master.pages.statusActive")
						: i18n.t("master.pages.statusInactive")}
				</span>
			</div>

			<div
				className="mp-card__featured"
				style={{
					background: `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`,
					border: `1px solid ${color}25`,
				}}
			>
				<div className="mp-card__featured-label" style={{ color }}>
					{i18n.t("master.plans.colPriceMonthly")}
				</div>
				<div className="mp-card__featured-price">{formatPrice(plan.priceMonthly)}</div>
				<div className="mp-card__featured-hint">{i18n.t("master.plans.perMonth")}</div>
			</div>

			<div className="mp-card__prices">
				{PERIOD_ROWS.map((row) => {
					const discount = discountVsMonthly(plan[row.key], plan.priceMonthly);
					return (
						<div key={row.key} className="mp-card__price-row">
							<span className="mp-card__price-label">{i18n.t(row.labelKey)}</span>
							<div className="mp-card__price-right">
								{discount != null && (
									<span className="mp-card__discount">-{discount}%</span>
								)}
								<span className="mp-card__price-value">{formatPrice(plan[row.key])}</span>
							</div>
						</div>
					);
				})}
			</div>

			<div className="mp-card__actions">
				<button
					type="button"
					className="mp-card__btn-edit"
					onClick={() => onEdit(plan)}
					onMouseEnter={(e) => {
						e.currentTarget.style.background = `${color}22`;
						e.currentTarget.style.borderColor = `${color}66`;
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.background = "";
						e.currentTarget.style.borderColor = "";
					}}
				>
					<IconEdit />
					{i18n.t("master.plans.edit")}
				</button>
				<button
					type="button"
					className="mp-card__btn-delete"
					disabled={deleting}
					title={i18n.t("master.plans.delete")}
					aria-label={i18n.t("master.plans.delete")}
					onClick={() => onDelete(plan)}
				>
					<IconTrash />
				</button>
			</div>
		</article>
	);
}

export default function MasterPlans() {
	const history = useHistory();
	const { user, loading } = useContext(AuthContext);
	const routeMeta = getMasterRouteMeta("/admin-master/plans");
	const [plans, setPlans] = useState([]);
	const [loadingPlans, setLoadingPlans] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [deletingId, setDeletingId] = useState(null);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [planToDelete, setPlanToDelete] = useState(null);

	const fetchPlans = useCallback(() => {
		setLoadingPlans(true);
		return api
			.get("/master/plans")
			.then(({ data }) => setPlans(data || []))
			.catch((err) => toastError(err))
			.finally(() => setLoadingPlans(false));
	}, []);

	useEffect(() => {
		if (loading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace("/");
			return;
		}
		fetchPlans();
	}, [user, loading, history, fetchPlans]);

	const sortedPlans = useMemo(
		() => [...plans].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
		[plans]
	);

	const activeCount = useMemo(
		() => plans.filter((p) => p.active).length,
		[plans]
	);
	const inactiveCount = plans.length - activeCount;

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setModalOpen(true);
	};

	const openEdit = (plan) => {
		setEditing(plan);
		setForm({
			name: plan.name || "",
			priceMonthly: toFormPrice(plan.priceMonthly),
			priceQuarterly: toFormPrice(plan.priceQuarterly),
			priceSemiannual: toFormPrice(plan.priceSemiannual),
			priceAnnual: toFormPrice(plan.priceAnnual),
			monthlyTokens: String(plan.monthlyTokens ?? ""),
			active: Boolean(plan.active),
		});
		setModalOpen(true);
	};

	const closeModal = () => {
		if (saving) return;
		setModalOpen(false);
		setEditing(null);
		setForm(emptyForm);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const payload = {
			name: form.name.trim(),
			priceMonthly: toPayloadPrice(form.priceMonthly),
			priceQuarterly: toPayloadPrice(form.priceQuarterly),
			priceSemiannual: toPayloadPrice(form.priceSemiannual),
			priceAnnual: toPayloadPrice(form.priceAnnual),
			monthlyTokens: Number(form.monthlyTokens),
			active: Boolean(form.active),
		};
		setSaving(true);
		try {
			if (editing) {
				await api.put(`/master/plans/${editing.id}`, payload);
				toast.success(i18n.t("master.plans.toastUpdated"));
			} else {
				await api.post("/master/plans", payload);
				toast.success(i18n.t("master.plans.toastCreated"));
			}
			setModalOpen(false);
			setEditing(null);
			setForm(emptyForm);
			await fetchPlans();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleOpenDeleteConfirm = (plan) => {
		setPlanToDelete(plan);
		setConfirmDeleteOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!planToDelete) return;
		setDeletingId(planToDelete.id);
		try {
			const { data } = await api.delete(`/master/plans/${planToDelete.id}`);
			if (data?.deleted) {
				toast.success(i18n.t("master.plans.toastDeleted"));
			} else {
				toast.success(i18n.t("master.plans.toastDisabled"));
			}
			setPlanToDelete(null);
			await fetchPlans();
		} catch (err) {
			toastError(err);
		} finally {
			setDeletingId(null);
		}
	};

	if (loading || !user || user.profile !== ROLES.master) {
		return null;
	}

	const modalFooter = (
		<>
			<button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
				{i18n.t("master.plans.cancel")}
			</button>
			<button
				type="submit"
				form="master-plan-form"
				className="btn-primary"
				disabled={saving}
			>
				{saving ? i18n.t("master.plans.saving") : i18n.t("master.plans.save")}
			</button>
		</>
	);

	const stats = [
		{ labelKey: "master.plans.statTotal", value: plans.length, color: "#6366f1" },
		{ labelKey: "master.plans.statActive", value: activeCount, color: "#10b981" },
		{ labelKey: "master.plans.statInactive", value: inactiveCount, color: "#6b7280" },
	];

	return (
		<div className="master-plans-page mx-auto w-full max-w-none box-border">
			<MasterPageBreadcrumb
				title={i18n.t(routeMeta.titleKey)}
				breadcrumbs={routeMeta.breadcrumbs}
			/>

			<div className="master-plans-page__top">
				<div>
					<h1 className="master-plans-page__title">{i18n.t("master.plans.pageTitle")}</h1>
					<p className="master-plans-page__subtitle">
						{i18n.t("master.plans.pageSubtitle")}
					</p>
				</div>
				<button type="button" className="master-plans-page__create" onClick={openCreate}>
					<IconPlus />
					{i18n.t("master.plans.create")}
				</button>
			</div>

			<div className="master-plans-page__stats">
				{stats.map((stat) => (
					<div key={stat.labelKey} className="master-plans-page__stat">
						<span
							className="master-plans-page__stat-dot"
							style={{
								background: stat.color,
								boxShadow: `0 0 6px ${stat.color}`,
							}}
						/>
						<span className="master-plans-page__stat-label">{i18n.t(stat.labelKey)}</span>
						<span className="master-plans-page__stat-value">{stat.value}</span>
					</div>
				))}
			</div>

			<div className="master-plans-page__section">
				<span className="master-plans-page__section-title">
					{i18n.t("master.plans.listTitle")}
				</span>
				<span className="master-plans-page__section-count">
					{plans.length} {i18n.t("master.plans.countLabel")}
				</span>
			</div>

			{loadingPlans ? (
				<div className="master-plans-page__loading">
					<MasterSpinner />
					<span>{i18n.t("master.plans.loading")}</span>
				</div>
			) : sortedPlans.length === 0 ? (
				<div className="master-plans-page__empty">
					<p className="master-plans-page__empty-title">{i18n.t("master.plans.empty")}</p>
					<p className="m-0 text-sm">{i18n.t("master.plans.emptyHint")}</p>
					<button type="button" className="master-plans-page__create mt-2" onClick={openCreate}>
						<IconPlus />
						{i18n.t("master.plans.create")}
					</button>
				</div>
			) : (
				<div className="master-plans-page__grid">
					{sortedPlans.map((plan, index) => (
						<PlanCard
							key={plan.id}
							plan={plan}
							color={planColor(index)}
							deleting={deletingId === plan.id}
							onEdit={openEdit}
							onDelete={handleOpenDeleteConfirm}
						/>
					))}
				</div>
			)}

			<MasterModal
				open={modalOpen}
				onClose={closeModal}
				title={
					editing
						? i18n.t("master.plans.editTitle")
						: i18n.t("master.plans.createTitle")
				}
				subtitle={i18n.t("master.plans.pricesHint")}
				footer={modalFooter}
				maxWidth={520}
			>
				<form id="master-plan-form" onSubmit={handleSubmit} className="space-y-1">
					<AuthField id="plan-name" label={i18n.t("master.plans.fieldName")}>
						<input
							id="plan-name"
							type="text"
							className="form-input w-full"
							required
							minLength={2}
							value={form.name}
							onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
							autoFocus
						/>
					</AuthField>

					<div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
						<AuthField
							id="plan-price-monthly"
							label={i18n.t("master.plans.fieldPriceMonthly")}
							className="mb-0"
						>
							<input
								id="plan-price-monthly"
								type="number"
								className="form-input w-full"
								min={0}
								step="0.01"
								value={form.priceMonthly}
								onChange={(e) =>
									setForm((f) => ({ ...f, priceMonthly: e.target.value }))
								}
							/>
						</AuthField>
						<AuthField
							id="plan-price-quarterly"
							label={i18n.t("master.plans.fieldPriceQuarterly")}
							className="mb-0"
						>
							<input
								id="plan-price-quarterly"
								type="number"
								className="form-input w-full"
								min={0}
								step="0.01"
								value={form.priceQuarterly}
								onChange={(e) =>
									setForm((f) => ({ ...f, priceQuarterly: e.target.value }))
								}
							/>
						</AuthField>
						<AuthField
							id="plan-price-semiannual"
							label={i18n.t("master.plans.fieldPriceSemiannual")}
							className="mb-0"
						>
							<input
								id="plan-price-semiannual"
								type="number"
								className="form-input w-full"
								min={0}
								step="0.01"
								value={form.priceSemiannual}
								onChange={(e) =>
									setForm((f) => ({ ...f, priceSemiannual: e.target.value }))
								}
							/>
						</AuthField>
						<AuthField
							id="plan-price-annual"
							label={i18n.t("master.plans.fieldPriceAnnual")}
							className="mb-0"
						>
							<input
								id="plan-price-annual"
								type="number"
								className="form-input w-full"
								min={0}
								step="0.01"
								value={form.priceAnnual}
								onChange={(e) =>
									setForm((f) => ({ ...f, priceAnnual: e.target.value }))
								}
							/>
						</AuthField>
					</div>

					<AuthField
						id="plan-monthly-tokens"
						label={i18n.t("master.plans.fieldMonthlyTokens")}
					>
						<input
							id="plan-monthly-tokens"
							type="number"
							className="form-input w-full"
							required
							min={0}
							step={1}
							value={form.monthlyTokens}
							onChange={(e) =>
								setForm((f) => ({ ...f, monthlyTokens: e.target.value }))
							}
						/>
					</AuthField>
					<AuthField>
						<label className="flex items-center gap-3 cursor-pointer mt-2">
							<input
								type="checkbox"
								className="w-5 h-5 rounded border-default-300 text-primary focus:ring-primary/30"
								checked={form.active}
								onChange={(e) =>
									setForm((f) => ({ ...f, active: e.target.checked }))
								}
							/>
							<span className="text-sm font-medium text-default-800 dark:text-default-200">
								{i18n.t("master.plans.fieldActive")}
							</span>
						</label>
					</AuthField>
				</form>
			</MasterModal>

			<MasterConfirmationModal
				title={
					planToDelete
						? i18n.t("master.plans.confirmDelete", { name: planToDelete.name })
						: i18n.t("master.plans.delete")
				}
				open={confirmDeleteOpen}
				onClose={setConfirmDeleteOpen}
				onConfirm={handleConfirmDelete}
			>
				{i18n.t("master.plans.confirmDeleteMessage")}
			</MasterConfirmationModal>
		</div>
	);
}
